/**
 * Contenido centralizado del sistema de ayuda del dashboard de negocio.
 * 
 * Estructura:
 *  - kpiTips: tooltips (?) para cada tarjeta KPI
 *  - tabTips: tooltips (?) para cada pestaña
 *  - tabBanners: banners informativos por pestaña (aparecen la primera vez)
 *  - bookviaTips: "Tips de Bookvia" contextuales por pestaña (modal completo)
 *  - faq: preguntas frecuentes agrupadas por categoría (botón flotante)
 *  - tourSteps: pasos del tour guiado inicial
 * 
 * Editar este archivo NO requiere tocar componentes.
 */

// ────────────────────────────────────────────────────────────────────
// KPI Tips — se muestran al tocar el ícono (?) al lado de cada métrica
// ────────────────────────────────────────────────────────────────────
export const kpiTips = {
  today: {
    title: 'Citas de hoy',
    description: 'Total de reservas programadas para hoy, sin importar el estado.',
    example: 'Si tienes 3 reservas confirmadas y 1 cancelada, aquí verás 3 (solo cuenta las activas).',
  },
  pending: {
    title: 'Confirmadas',
    description: 'Reservas ya pagadas o aceptadas por ti. Cuentan para tus ingresos.',
    example: 'Cuando un cliente paga el depósito, su reserva pasa de "Pendiente" a "Confirmada" automáticamente.',
  },
  revenue: {
    title: 'Ingresos del mes',
    description: 'Suma de todos los pagos que has recibido en el mes calendario actual.',
    example: 'Se actualiza en tiempo real. Para reportes detallados por servicio o empleado, ve a "Reportes".',
  },
  unique_customers: {
    title: 'Clientes del mes',
    description: 'Cantidad de personas distintas que han reservado contigo este mes.',
    example: 'Si un mismo cliente reservó 3 veces, cuenta como 1.',
  },
  total: {
    title: 'Total de citas',
    description: 'Contador histórico de todas las reservas recibidas desde que abriste tu negocio en Bookvia.',
  },
};

// ────────────────────────────────────────────────────────────────────
// Tab Tips — tooltip (?) al lado del nombre de cada pestaña
// ────────────────────────────────────────────────────────────────────
export const tabTips = {
  overview: {
    title: 'Agenda',
    description: 'Tu calendario de reservas. Ves las citas del día seleccionado y su estado.',
  },
  reports: {
    title: 'Reportes',
    description: 'Análisis detallado de tus ingresos, servicios más vendidos y clientes frecuentes.',
    example: 'Descubre a qué hora tienes más demanda y qué servicio genera más ingresos.',
  },
  clients: {
    title: 'Clientes',
    description: 'Base de datos de todos los clientes que han reservado contigo.',
    example: 'Puedes ver el historial, frecuencia y ticket promedio de cada uno.',
  },
  services: {
    title: 'Servicios',
    description: 'Qué vendes en tu negocio. Sin servicios configurados, los clientes NO pueden reservar contigo.',
    example: 'Corte de cabello - 30 min - $200 · Manicure - 45 min - $150',
  },
  team: {
    title: 'Equipo',
    description: 'Agrega a tus empleados/estilistas. Cada uno puede tener horarios propios y recibir sus propias citas.',
  },
  closures: {
    title: 'Cierres',
    description: 'Días u horarios en los que tu negocio NO trabaja: vacaciones, feriados, capacitaciones.',
    example: 'Bloquea el 24-25 de diciembre y ningún cliente podrá reservar esos días.',
  },
  photos: {
    title: 'Fotos',
    description: 'Galería de tu negocio. Suele ser el primer contacto visual del cliente antes de reservar.',
    example: 'Fotos profesionales aumentan tus reservas hasta 40%. Sube mínimo 5 fotos.',
  },
  subscription: {
    title: 'Suscripción',
    description: 'Estado de tu plan de Bookvia. Aquí facturas, cambias de plan o cancelas tu suscripción.',
  },
  activity: {
    title: 'Actividad',
    description: 'Historial de acciones importantes: cambios de perfil, aprobaciones, disputas, etc.',
  },
};

// ────────────────────────────────────────────────────────────────────
// Banners informativos por pestaña (se cierran con ✕ y no vuelven)
// ────────────────────────────────────────────────────────────────────
export const tabBanners = {
  overview: 'En Agenda ves todas las citas del día seleccionado. Toca una fecha del calendario para ver sus reservas.',
  reports: 'Aquí encuentras análisis avanzados: ingresos por semana, servicios top y clientes que más te compran.',
  clients: 'Todos tus clientes en un solo lugar. Puedes ver su historial completo y contactarlos directamente.',
  services: 'Configura tus servicios con precio y duración. Los clientes solo pueden reservar los servicios que actives aquí.',
  team: 'Agrega a los miembros de tu equipo. Cada uno tendrá su propio calendario, servicios y estadísticas.',
  closures: 'Bloquea días completos o rangos de horas. Nadie podrá reservar durante ese tiempo.',
  photos: 'Sube fotos reales y profesionales de tu negocio. Es lo primero que ve un cliente antes de reservar.',
  subscription: 'Gestiona tu plan de Bookvia. Puedes facturar, cambiar de plan o cancelar cuando quieras.',
  activity: 'Historial cronológico de todo lo que ha pasado con tu cuenta y negocio.',
};

// Category-specific overrides. Keyed as `${tabKey}_${super_group}`.
// TabHelpHeader picks the variant if present, otherwise falls back to tabBanners.
export const tabBannersByCategory = {
  services_eventos:
    'Configura los servicios que ofreces en tu salón: banquete, sillas, meseros, mago, payaso, DJ, decoración, vajilla, servicio completo… Cada uno con su precio y duración.',
  services_fitness:
    'Configura las clases y membresías que ofreces: clase individual, mensualidad, sesión con entrenador personal, clase grupal. Cada una con su precio y duración.',
  services_mascotas:
    'Configura los servicios para mascotas: baño y corte, consulta veterinaria, vacunación, guardería, adiestramiento. Cada uno con su precio y duración.',
  services_consultoria:
    'Configura los servicios de asesoría que ofreces: sesión de 1 hora, plan mensual, consultoría por proyecto, retainer. Cada uno con su precio y duración.',
  services_aprendizaje:
    'Configura tus clases y cursos: clase individual, curso completo, paquete de 10 clases, taller intensivo. Cada uno con su precio y duración.',
  services_salud:
    'Configura las consultas y estudios que ofreces: consulta general, chequeo, estudio de laboratorio, tratamiento. Cada uno con su precio y duración.',
};

// ────────────────────────────────────────────────────────────────────
// Tips de Bookvia (contextuales por pestaña) — modal completo con tips
// ────────────────────────────────────────────────────────────────────
export const bookviaTips = {
  overview: {
    label: 'Agenda',
    headline: 'Cómo conseguir más citas',
    tips: [
      { icon: '📱', title: 'Comparte tu perfil por WhatsApp', body: 'Toca "Compartir" arriba y mándale tu link a tus clientes actuales. Es la forma #1 de conseguir tus primeras 10 reservas.' },
      { icon: '📸', title: 'Sube al menos 5 fotos profesionales', body: 'Negocios con fotos de calidad reciben hasta 40% más reservas. Ve a la pestaña "Fotos".' },
      { icon: '⭐', title: 'Pide reseñas a tus mejores clientes', body: 'Después de una cita completada, pídele al cliente que te deje una reseña en Bookvia. 5 reseñas ayudan a subir en el ranking.' },
      { icon: '⏰', title: 'Ofrece horarios flexibles al inicio', body: 'Sábados/domingos y horarios nocturnos tienen menos competencia. Considera abrir esos horarios los primeros meses.' },
    ],
  },
  reports: {
    label: 'Reportes',
    headline: 'Cómo aprovechar tus reportes',
    tips: [
      { icon: '📈', title: 'Identifica tu servicio estrella', body: 'Ordena por "más vendido" y descubre qué servicio te trae más ingresos. Considera enfocarte más en promocionarlo.' },
      { icon: '📅', title: 'Detecta tu día pico', body: 'Ve qué día de la semana tienes más reservas. Ese día no des descansos a tu equipo.' },
      { icon: '🕐', title: 'Optimiza horas muertas', body: 'Si notas horas con pocas citas, ofrece promociones específicas para esos horarios.' },
    ],
  },
  clients: {
    label: 'Clientes',
    headline: 'Cómo fidelizar clientes',
    tips: [
      { icon: '💎', title: 'Identifica a tus mejores clientes', body: 'Ordena por "más frecuentes" y contáctalos para pedir reseñas o darles promociones exclusivas.' },
      { icon: '🎂', title: 'Aprovecha cumpleaños', body: 'Ofrece un descuento especial en el mes de cumpleaños de cada cliente. Genera lealtad.' },
      { icon: '💬', title: 'Responde reseñas negativas', body: 'Una respuesta profesional a una reseña negativa demuestra que te importa. Los futuros clientes lo notan.' },
    ],
  },
  services: {
    label: 'Servicios',
    headline: 'Cómo poner precios y describir servicios',
    tips: [
      { icon: '💵', title: 'Investiga precios de tu competencia', body: 'Busca 3-5 negocios similares en Bookvia y compara precios. Ajusta los tuyos para ser competitivo sin devaluar tu trabajo.' },
      { icon: '⏱️', title: 'Sé realista con las duraciones', body: 'Si un corte real toma 45 min, no pongas 30 min. Los clientes se molestan cuando esperan más de lo prometido.' },
      { icon: '📦', title: 'Ofrece paquetes/combos', body: 'Combos como "Corte + Barba" con 10% descuento aumentan el ticket promedio y fidelizan clientes.' },
      { icon: '📝', title: 'Escribe descripciones claras', body: 'Explica qué incluye cada servicio. "Manicure" es genérico. "Manicure con esmalte semipermanente, incluye limado y cutícula" es específico.' },
    ],
  },
  team: {
    label: 'Equipo',
    headline: 'Cómo organizar tu equipo',
    tips: [
      { icon: '👥', title: 'Asigna horarios individuales', body: 'Cada miembro puede tener su propio horario. Configúralo bien para evitar conflictos de citas.' },
      { icon: '🎯', title: 'Especialización por empleado', body: 'Si uno de tu equipo es experto en tintes, asígnale solo ese servicio. Los clientes valoran la especialización.' },
      { icon: '📊', title: 'Revisa el desempeño individual', body: 'En Reportes puedes ver qué empleado tiene más reservas y mejores reseñas.' },
    ],
  },
  closures: {
    label: 'Cierres',
    headline: 'Buenas prácticas para bloqueos',
    tips: [
      { icon: '📆', title: 'Bloquea con anticipación', body: 'Si sabes que estarás cerrado en fechas específicas (vacaciones, feriados), bloquéalas con semanas de anticipación para no dejar clientes esperando.' },
      { icon: '📢', title: 'Avisa a clientes recurrentes', body: 'Antes de cerrar por vacaciones, envía un mensaje por WhatsApp a tus clientes frecuentes para que reagenden.' },
      { icon: '🕐', title: 'Bloqueos parciales también sirven', body: 'Puedes bloquear solo unas horas del día (ejemplo: 12-2pm para almuerzo). No tiene que ser el día completo.' },
    ],
  },
  photos: {
    label: 'Fotos',
    headline: 'Cómo tomar mejores fotos',
    tips: [
      { icon: '☀️', title: 'Usa luz natural', body: 'Toma fotos de día cerca de una ventana. Es gratis y se ve profesional.' },
      { icon: '📐', title: 'Mínimo 5 fotos, máximo 15', body: 'Muestra: fachada, interior, resultados de tu trabajo, tu equipo. Variedad genera confianza.' },
      { icon: '❌', title: 'Evita fotos de stock', body: 'Los clientes detectan fotos genéricas. Tus fotos reales generan más confianza.' },
      { icon: '📱', title: 'El celular es suficiente', body: 'No necesitas cámara profesional. Un celular moderno con buena luz basta.' },
    ],
  },
  subscription: {
    label: 'Suscripción',
    headline: 'Cómo aprovechar tu plan',
    tips: [
      { icon: '📄', title: 'Descarga tus facturas', body: 'Todas tus facturas de Bookvia están disponibles para descargar. Úsalas para tu contabilidad.' },
      { icon: '🔄', title: 'Cambia de plan cuando crezcas', body: 'Si tu negocio crece y necesitas más funciones, puedes cambiar de plan en cualquier momento.' },
    ],
  },
  activity: {
    label: 'Actividad',
    headline: 'Cómo interpretar tu actividad',
    tips: [
      { icon: '📋', title: 'Revisa acciones importantes', body: 'El historial de actividad te ayuda a saber qué cambios se han hecho en tu negocio y cuándo.' },
      { icon: '🔍', title: 'Filtra por tipo de acción', body: 'Puedes ver solo cambios de precio, aprobaciones, o disputas para investigar temas específicos.' },
    ],
  },
};

// Category-specific overrides for the "Tips de Bookvia" modal.
// Keyed as `${tabKey}_${super_group}`; TabHelpHeader picks the variant if
// present, otherwise falls back to the base bookviaTips entry.
export const bookviaTipsByCategory = {
  services_eventos: {
    label: 'Servicios',
    headline: 'Cómo estructurar los servicios de tu salón',
    tips: [
      { icon: '🍽️', title: 'Divide banquete de renta', body: 'Sepáralos: "Banquete 100 personas", "Renta del salón", "Sillas y mesas", "Servicio de meseros". Los clientes pueden armar su paquete.' },
      { icon: '🎭', title: 'Incluye entretenimiento', body: 'Ofrece extras: mago, payaso, DJ, mariachi, show infantil. Cada uno como servicio aparte con su precio.' },
      { icon: '💐', title: 'Detalla la decoración', body: 'Ejemplo: "Decoración básica" (globos, centros de mesa) vs "Decoración premium" (flores naturales, iluminación LED). Ayuda al cliente a elegir.' },
      { icon: '📅', title: 'Menciona duración del evento', body: 'La duración del servicio debe ser realista para eventos: 4 horas, 6 horas, todo el día. Evita que se te empalmen fechas.' },
      { icon: '💰', title: 'Ofrece paquetes completos', body: 'Ejemplo "Paquete XV años" que incluya salón + banquete + DJ + decoración con 10% descuento. Aumenta el ticket promedio.' },
      { icon: '📝', title: 'Descripciones específicas', body: '"Banquete" es genérico. "Banquete 3 tiempos: crema, pollo relleno, postre, agua y café ilimitados" es específico y vende más.' },
    ],
  },
  services_fitness: {
    label: 'Servicios',
    headline: 'Cómo estructurar clases y membresías',
    tips: [
      { icon: '🏋️', title: 'Clase individual vs paquete', body: 'Ofrece "Clase suelta" ($150) y "Paquete 10 clases" ($1200). Los paquetes fidelizan y aseguran ingresos.' },
      { icon: '📅', title: 'Membresía mensual/anual', body: 'La membresía mensual da flujo predecible. Descuenta 15% en la anual para reducir la rotación.' },
      { icon: '👤', title: 'Sesiones con entrenador', body: 'Separa "Clase grupal" de "Sesión personalizada". La personalizada cobra más (usualmente 3-5x).' },
      { icon: '⏱️', title: 'Duración realista', body: 'Yoga 60 min, HIIT 45 min, spinning 50 min. Sé exacto para que los clientes planeen bien.' },
    ],
  },
  services_mascotas: {
    label: 'Servicios',
    headline: 'Cómo estructurar servicios para mascotas',
    tips: [
      { icon: '🐕', title: 'Separa por tamaño', body: 'El baño de un chihuahua no cuesta igual que un San Bernardo. Ofrece "Baño pequeño / mediano / grande / gigante" con precios distintos.' },
      { icon: '✂️', title: 'Combina baño + corte', body: 'Muchos clientes buscan "Baño y corte" como paquete completo. Ofrécelo con 10% descuento vs contratarlos por separado.' },
      { icon: '💉', title: 'Consultas veterinarias', body: 'Divide "Consulta general", "Vacunación", "Desparasitación", "Certificado de salud". Cada uno con su precio y duración.' },
      { icon: '🏠', title: 'Servicio a domicilio', body: 'Si haces baños en casa del cliente, cóbralo como servicio premium (+30-50% sobre el precio del local).' },
    ],
  },
  services_consultoria: {
    label: 'Servicios',
    headline: 'Cómo estructurar sesiones de asesoría',
    tips: [
      { icon: '🕐', title: 'Sesión por hora vs plan', body: 'Ofrece "Sesión de 1 hora" ($1500) y "Plan mensual - 4 sesiones" ($5000). El plan da descuento y compromiso.' },
      { icon: '📞', title: 'Presencial vs videollamada', body: 'Sepáralas: la videollamada suele ser 20-30% más económica y llega a más clientes.' },
      { icon: '📄', title: 'Servicios de "producto"', body: 'Ejemplo: "Diagnóstico inicial", "Plan estratégico", "Reporte mensual". Son entregables con precio fijo, no por hora.' },
      { icon: '🎯', title: 'Especialidad clara', body: '"Consultoría de negocios" es genérico. "Consultoría de finanzas para PYMES" atrae al cliente correcto.' },
    ],
  },
  services_aprendizaje: {
    label: 'Servicios',
    headline: 'Cómo estructurar clases y cursos',
    tips: [
      { icon: '📚', title: 'Clase suelta vs curso', body: 'Ofrece "Clase individual" ($300) y "Curso completo 20 clases" ($4500). El curso baja el costo por clase y asegura el compromiso.' },
      { icon: '👥', title: 'Individual vs grupal', body: 'La clase individual cobra 2-3x más pero es más flexible. La grupal fideliza pero exige llenar cupos.' },
      { icon: '🌐', title: 'Presencial vs online', body: 'Sepáralas para que el cliente elija. Las online amplían tu mercado geográfico.' },
      { icon: '🎓', title: 'Nivel del alumno', body: 'Divide por nivel: "Principiante", "Intermedio", "Avanzado". Evita que un alumno pague por algo que no le sirve.' },
    ],
  },
  services_salud: {
    label: 'Servicios',
    headline: 'Cómo estructurar consultas y estudios',
    tips: [
      { icon: '🩺', title: 'Consulta primera vs seguimiento', body: 'La primera consulta suele durar más (45-60 min) y cobrar más. El seguimiento (20-30 min) es más económico.' },
      { icon: '🧪', title: 'Estudios y análisis', body: 'Divide "Consulta" de "Estudios" (laboratorio, imagen, biopsias). Cada uno con precio y tiempo estimado.' },
      { icon: '💊', title: 'Tratamientos completos', body: 'Ofrece paquetes: "Tratamiento de 6 sesiones" con descuento vs sesiones individuales. Aumenta la adherencia del paciente.' },
      { icon: '🏥', title: 'Modalidad clara', body: 'Especifica si es presencial, videoconsulta o a domicilio. Cada uno con precio distinto.' },
    ],
  },
};

// ────────────────────────────────────────────────────────────────────
// FAQ del botón flotante — organizado por categorías
// ────────────────────────────────────────────────────────────────────
export const faqCategories = [
  {
    category: 'Configuración inicial',
    items: [
      {
        q: '¿Cómo configuro mis horarios de atención?',
        a: 'Ve a "Config" (arriba a la derecha en la web) → "Horarios". Elige los días y horas de cada día. Puedes tener diferentes horarios para cada día de la semana.',
      },
      {
        q: '¿Cómo agrego mis servicios?',
        a: 'En la pestaña "Servicios" toca el botón "+ Agregar servicio". Coloca nombre, duración (en minutos) y precio. Puedes tener servicios ilimitados.',
      },
      {
        q: '¿Cómo agrego a mis empleados?',
        a: 'En la pestaña "Equipo" toca "+ Agregar miembro". Cada empleado puede tener sus propios horarios y recibir sus propias citas.',
      },
      {
        q: '¿Cómo cambio mi foto de perfil o portada?',
        a: 'Toca "Ver perfil" arriba, y luego el ícono de cámara en la portada o el avatar. Sube una foto de alta calidad.',
      },
    ],
  },
  {
    category: 'Reservas y citas',
    items: [
      {
        q: '¿Qué significa cada color en el calendario?',
        a: 'Azul = Confirmada · Verde = Completada · Amarillo = Pendiente de pago · Rojo = Cancelada.',
      },
      {
        q: '¿Qué pasa si un cliente no llega?',
        a: 'Puedes reportar "cliente no llegó" desde la cita. Bookvia analiza el historial y decide si el cliente pierde su depósito automáticamente.',
      },
      {
        q: '¿Puedo cancelar una cita yo mismo?',
        a: 'Sí. Abre la cita → toca los tres puntos → "Cancelar". Si el cliente ya pagó depósito, Bookvia le devuelve el dinero automáticamente.',
      },
      {
        q: '¿Cómo reagendo una cita?',
        a: 'Abre la cita → toca "Reagendar" → elige nueva fecha/hora. El cliente recibe notificación automática del cambio.',
      },
    ],
  },
  {
    category: 'Pagos e ingresos',
    items: [
      {
        q: '¿Cuándo recibo mi dinero?',
        a: 'Los pagos entran a tu cuenta bancaria de Stripe automáticamente 2 días hábiles después de la cita completada.',
      },
      {
        q: '¿Qué comisión cobra Bookvia?',
        a: 'Bookvia cobra una comisión pequeña sobre cada depósito exitoso. Ve los detalles exactos en "Suscripción".',
      },
      {
        q: '¿Dónde veo mis ganancias?',
        a: 'La tarjeta "Ingresos mes" arriba del dashboard te da el resumen. Para reportes detallados: pestaña "Reportes".',
      },
      {
        q: '¿Cómo cambio mi cuenta bancaria de Stripe?',
        a: 'En Config → Stripe Connect → "Ir a mi cuenta Stripe". Ahí actualizas tu cuenta bancaria desde el portal oficial de Stripe.',
      },
    ],
  },
  {
    category: 'Perfil y marketing',
    items: [
      {
        q: '¿Cómo hago que mi negocio aparezca primero en Bookvia?',
        a: 'Completa tu perfil al 100%, sube mínimo 5 fotos de calidad, define servicios con precios claros y responde reseñas. Bookvia prioriza negocios completos y activos.',
      },
      {
        q: '¿Cómo comparto mi perfil con mis clientes?',
        a: 'Botón "Compartir" arriba del dashboard. Copia el link o envíalo directo por WhatsApp a tus clientes.',
      },
      {
        q: '¿Cómo pido reseñas a mis clientes?',
        a: 'Después de una cita completada, Bookvia envía automáticamente una invitación al cliente para dejar reseña. También puedes recordarles personalmente.',
      },
    ],
  },
  {
    category: 'Problemas comunes',
    items: [
      {
        q: 'No me llegan notificaciones push',
        a: 'Verifica que la app tenga permiso de notificaciones (Ajustes del teléfono → Bookvia → Notificaciones). En el navegador web activa las notificaciones del sitio.',
      },
      {
        q: 'Un cliente reservó pero no recibí el pago',
        a: 'Si la reserva está en "Pendiente de pago" (amarillo), el cliente aún no ha completado el depósito. Cuando pague pasará a "Confirmada" y verás el dinero en Stripe 2 días después.',
      },
      {
        q: 'Quiero cambiar la dirección de mi negocio',
        a: 'Ve a Config → Datos del negocio → Dirección. El cambio se aplica inmediatamente en tu perfil.',
      },
    ],
  },
];

// ────────────────────────────────────────────────────────────────────
// Tour guiado — se lanza automáticamente la primera vez que entra el dueño
// ────────────────────────────────────────────────────────────────────
export const tourSteps = [
  {
    title: '👋 ¡Bienvenido a tu Dashboard!',
    body: 'Este es tu centro de control. Desde aquí gestionas todo tu negocio en Bookvia. Vamos a hacer un tour rápido — solo 6 pasos.',
  },
  {
    title: '📊 Tus métricas al día',
    body: 'Estas tarjetas te dan un resumen instantáneo: citas de hoy, confirmadas, ingresos del mes y total histórico. Toca cualquier tarjeta para ver el detalle.',
  },
  {
    title: '📅 Agenda',
    body: 'Tu calendario de reservas. Toca una fecha para ver todas las citas de ese día. Los colores indican el estado (confirmada, completada, cancelada, etc.).',
  },
  {
    title: '💇 Servicios y Equipo',
    body: 'En "Servicios" configuras qué vendes y a qué precio. En "Equipo" agregas a tus empleados con sus propios horarios y calendarios.',
  },
  {
    title: '💡 Tips de Bookvia',
    body: 'En cada pestaña verás un botón "Tips de Bookvia" con consejos prácticos para crecer tu negocio (conseguir más citas, poner precios, tomar mejores fotos).',
  },
  {
    title: '❓ Ayuda siempre a la mano',
    body: 'Cuando tengas dudas: toca el ícono (?) al lado de cada elemento, o el botón coral flotante abajo a la derecha para buscar en las preguntas frecuentes.',
  },
];
